/**
 * Hook for fetching unread notification count with polling
 */
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/hooks/useAuth'
import { useAdapter } from '@/hooks/useAdapter'
import { usePlatformGuard } from '@/hooks/usePlatformGuard'
import { accountQueryIdentifier } from '@/lib/accountQueryIdentifier'

interface UseUnreadCountOptions {
    enabled?: boolean
    pollingInterval?: number
}

const DEFAULT_POLLING_INTERVAL = 60 * 1000 // 60 seconds

export function useUnreadCount(options: UseUnreadCountOptions = {}) {
    const { enabled = true, pollingInterval = DEFAULT_POLLING_INTERVAL } = options
    const { adapter, session: adapterSession, platform: selectedProtocol } = useAdapter()
    const { session, mastodonSession, pixelfedSession, nostrSession } = useAuth()
    const isCompatible = usePlatformGuard('atproto')

    // Stable user identifier for cache key differentiation across accounts
    const userIdentifier = accountQueryIdentifier(selectedProtocol, {
        session,
        mastodonSession,
        pixelfedSession,
        nostrSession,
    })

    const query = useQuery<{ count: number }, Error>({
        queryKey: ['unreadCount', selectedProtocol, userIdentifier],
        queryFn: async () => {
            if (!adapterSession) throw new Error('No session')
            const count = await adapter.getUnreadCount(adapterSession)
            return { count }
        },
        enabled: enabled && !!adapterSession && isCompatible,
        staleTime: 30 * 1000, // 30 seconds
        gcTime: 5 * 60 * 1000, // 5 minutes
        refetchInterval: pollingInterval,
        refetchIntervalInBackground: false, // Don't poll when tab is not visible
    })

    return {
        count: query.data?.count ?? 0,
        isLoading: query.isLoading,
        error: query.error,
        refetch: query.refetch,
    }
}
